import type { ReactNode } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { NoBackend } from "@/components/common/NoBackend";
import { QuickActionLink, StatTile } from "@/components/dashboard/StatTile";
import { Hero } from "@/components/layout/Hero";
import {
  CockpitDashboardDocument,
  HomeSaveInsightsDocument,
  MyselfDocument,
} from "@/graphql/generated/operations";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";

// Save landing. Hero + an Identity Snapshot of real vault data (StatTile grid,
// same design as the Home dashboard): profile completeness, recent profile
// visitors, contacts and pins. The remaining sections stay design-spec stubs.

// The profile fields that count toward "completeness". Kept small and
// meaningful; each missing one is a nudge to enrich the vault.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function profileCompleteness(m: any): { filled: number; total: number; missing: string[] } {
  const checks: { label: string; ok: boolean }[] = [
    { label: "profession", ok: Boolean(m?.profession) },
    { label: "city", ok: Boolean(m?.city) },
    { label: "contactData", ok: Boolean(m?.email || m?.phone) },
    { label: "bio", ok: Boolean(m?.bio) },
    { label: "experience", ok: (m?.experiences?.length ?? 0) > 0 },
    { label: "education", ok: (m?.educations?.length ?? 0) > 0 },
    { label: "expertise", ok: (m?.expertises?.length ?? 0) > 0 },
    { label: "languages", ok: (m?.languageSkills?.length ?? 0) > 0 },
  ];
  return {
    filled: checks.filter((c) => c.ok).length,
    total: checks.length,
    missing: checks.filter((c) => !c.ok).map((c) => c.label),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function visitorName(v: any): string {
  if (v?.person?.formattedName) return v.person.formattedName;
  const given = (v?.person?.name?.given ?? []).filter(Boolean).join(" ");
  return [given, v?.person?.name?.family].filter(Boolean).join(" ") || "–";
}

export function SavePage() {
  const navigate = useNavigate();
  const intl = useIntl();

  const missingLabels: Record<string, string> = {
    profession: intl.formatMessage({ id: "save.landing.fieldProfession", defaultMessage: "Profession" }),
    city: intl.formatMessage({ id: "save.landing.fieldCity", defaultMessage: "Place of residence" }),
    contactData: intl.formatMessage({ id: "save.landing.fieldContactData", defaultMessage: "Contact details" }),
    bio: intl.formatMessage({ id: "save.landing.fieldBio", defaultMessage: "Bio" }),
    experience: intl.formatMessage({ id: "save.landing.fieldExperience", defaultMessage: "Work experience" }),
    education: intl.formatMessage({ id: "save.landing.fieldEducation", defaultMessage: "Education" }),
    expertise: intl.formatMessage({ id: "save.landing.fieldExpertise", defaultMessage: "Expertise" }),
    languages: intl.formatMessage({ id: "save.landing.fieldLanguages", defaultMessage: "Languages" }),
  };

  const myselfQ = useQuery(MyselfDocument);
  const cockpitQ = useQuery(CockpitDashboardDocument);
  const insightsQ = useQuery(HomeSaveInsightsDocument);

  const completeness = profileCompleteness(myselfQ.data?.myself);
  const completenessPct = Math.round((completeness.filled / completeness.total) * 100);

  const visitors = cockpitQ.data?.recentProfileVisitors ?? [];
  const contactCount = insightsQ.data?.myContactCount ?? cockpitQ.data?.myContactCount ?? 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const groupsOut: any = insightsQ.data?.personGroups;
  const communityCount = groupsOut?.personGroups?.length ?? 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pins: any = insightsQ.data?.numberOfPinnedItems;
  const pinTotal =
    pins?.__typename === "NumberOfPinsResult"
      ? (pins.pinnedPersons ?? 0) + (pins.pinnedProjects ?? 0) + (pins.pinnedPosts ?? 0)
      : 0;

  // Guided tour (/tour): summary + the Identity Snapshot tiles, with live numbers.
  useAssistantTour(
    myselfQ.data
      ? {
          label: intl.formatMessage({
            id: "save.tour.label",
            defaultMessage: "Save",
          }),
          summary: intl.formatMessage(
            {
              id: "save.tour.summary",
              defaultMessage:
                "This is your Save area, your personal data vault. Your profile is {pct}% complete. Let me show you your vault at a glance.",
            },
            { pct: completenessPct },
          ),
          stops: [
            {
              anchor: "save.completeness",
              title: intl.formatMessage({ id: "save.tour.completeness.title", defaultMessage: "Profile completeness" }),
              description: intl.formatMessage(
                {
                  id: "save.tour.completeness.desc",
                  defaultMessage:
                    "Your profile is {pct}% complete.{missing} Click the tile to fill in the gaps.",
                },
                {
                  pct: completenessPct,
                  missing: completeness.missing.length
                    ? " " +
                      intl.formatMessage(
                        { id: "save.tour.completeness.missing", defaultMessage: "Still open: {fields}." },
                        { fields: completeness.missing.map((k) => missingLabels[k] ?? k).join(", ") },
                      )
                    : " " +
                      intl.formatMessage({
                        id: "save.tour.completeness.done",
                        defaultMessage: "Everything is filled in.",
                      }),
                },
              ),
            },
            {
              anchor: "save.visitors",
              title: intl.formatMessage({ id: "save.tour.visitors.title", defaultMessage: "Profile visitors" }),
              description: intl.formatMessage(
                {
                  id: "save.tour.visitors.desc",
                  defaultMessage:
                    "{count} recent profile visitors{last}. Open a name to see who is interested in you.",
                },
                {
                  count: visitors.length,
                  last: visitors.length ? ` — ${intl.formatMessage(
                    { id: "save.landing.lastVisitor", defaultMessage: "last: {name}" },
                    { name: visitorName(visitors[0]) },
                  )}` : "",
                },
              ),
            },
            {
              anchor: "save.community",
              title: intl.formatMessage({ id: "save.tour.community.title", defaultMessage: "Community" }),
              description: intl.formatMessage(
                {
                  id: "save.tour.community.desc",
                  defaultMessage:
                    "You have {contacts} contacts in {communities} communities. Click the tile to group and manage them.",
                },
                { contacts: contactCount, communities: communityCount },
              ),
            },
            {
              anchor: "save.pins",
              title: intl.formatMessage({ id: "save.tour.pins.title", defaultMessage: "Pins" }),
              description: intl.formatMessage(
                {
                  id: "save.tour.pins.desc",
                  defaultMessage:
                    "{count} pinned items: people, posts and projects you saved. Click the tile to open your pinboard.",
                },
                { count: pinTotal },
              ),
            },
          ],
        }
      : null,
  );

  // Live assistant context (motive 1, ADR 0020): the user's own Save overview.
  useAssistantContext(
    myselfQ.data
      ? [
          {
            description: "Save overview the user is viewing",
            value:
              `profile ${completenessPct}% complete` +
              (completeness.missing.length
                ? `; still missing: ${completeness.missing.map((k) => missingLabels[k] ?? k).join(", ")}`
                : "; profile complete") +
              `; ${contactCount} contacts, ${communityCount} communities, ${pinTotal} pinned items`,
          },
        ]
      : null,
  );

  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <Hero
        title="SAVE"
        subtitle={
          <>
            <FormattedMessage id="save.landing.heroSubtitleLine1" defaultMessage="Own your data." />
            <br />
            <FormattedMessage id="save.landing.heroSubtitleLine2" defaultMessage="Share on your terms." />
          </>
        }
        body={intl.formatMessage({
          id: "save.landing.heroBody",
          defaultMessage:
            "Your personal vault, transparent, verified, exportable. Profiles, powers, career, contacts, pins and favorites in one place.",
        })}
        image={{ src: "/heroes/save.jpg" }}
        cta={{
          kind: "button",
          label: `${intl.formatMessage({ id: "save.landing.heroCta", defaultMessage: "Open my Vault" })} ↗`,
          onClick: () => navigate("/save/basics"),
        }}
      />

      <SectionH
        label={intl.formatMessage({ id: "save.landing.identitySnapshotLabel", defaultMessage: "Identity Snapshot" })}
        right={intl.formatMessage({
          id: "save.landing.identitySnapshotRight",
          defaultMessage: "Your vault at a glance",
        })}
      >
        <div className="grid grid-cols-2 items-start gap-3 @sm:grid-cols-4">
          <div data-assistant-anchor="save.completeness">
          <StatTile
            to="/save/basics"
            label={intl.formatMessage({
              id: "save.landing.profileCompletenessLabel",
              defaultMessage: "Profile completeness",
            })}
            value={`${completenessPct} %`}
            bar={completenessPct}
            sub={
              completeness.missing.length
                ? `${intl.formatMessage({ id: "save.landing.openColon", defaultMessage: "Open" })}: ${completeness.missing
                    .slice(0, 2)
                    .map((k) => missingLabels[k] ?? k)
                    .join(", ")}${completeness.missing.length > 2 ? " …" : ""}`
                : intl.formatMessage({ id: "save.landing.complete", defaultMessage: "complete" })
            }
            loading={myselfQ.loading && !myselfQ.data}
            actions={
              <QuickActionLink to="/save/basics" icon="→">
                <FormattedMessage id="save.landing.enrichProfile" defaultMessage="Complete profile" />
              </QuickActionLink>
            }
          />
          </div>
          <div data-assistant-anchor="save.visitors">
          <StatTile
            to={visitors[0]?.person?.id ? `/p/${visitors[0].person.id}` : "/save"}
            label={intl.formatMessage({
              id: "save.landing.profileVisitorsLabel",
              defaultMessage: "Profile visitors",
            })}
            value={visitors.length}
            sub={
              visitors.length
                ? intl.formatMessage(
                    { id: "save.landing.lastVisitor", defaultMessage: "last: {name}" },
                    { name: visitorName(visitors[0]) },
                  )
                : intl.formatMessage({ id: "save.landing.noVisitsYet", defaultMessage: "no views yet" })
            }
            loading={cockpitQ.loading && !cockpitQ.data}
            actions={
              visitors.length ? (
                <>
                  {visitors.slice(0, 4).map((v) => (
                    <QuickActionLink key={v.person.id} to={`/p/${v.person.id}`} icon="→">
                      {visitorName(v)}
                    </QuickActionLink>
                  ))}
                </>
              ) : undefined
            }
          />
          </div>
          <div data-assistant-anchor="save.community">
          <StatTile
            to="/save/contact"
            label={intl.formatMessage({ id: "save.landing.communityLabel", defaultMessage: "Community" })}
            value={contactCount}
            sub={
              communityCount
                ? intl.formatMessage(
                    {
                      id: "save.landing.communityCount",
                      defaultMessage: "{count, plural, one {# community} other {# communities}}",
                    },
                    { count: communityCount },
                  )
                : contactCount
                  ? intl.formatMessage({ id: "save.landing.contacts", defaultMessage: "Contacts" })
                  : intl.formatMessage({ id: "save.landing.noContactsYet", defaultMessage: "no contacts yet" })
            }
            loading={insightsQ.loading && !insightsQ.data}
            actions={
              <QuickActionLink to="/save/contact" icon="→">
                <FormattedMessage id="save.landing.viewCommunities" defaultMessage="View communities" />
              </QuickActionLink>
            }
          />
          </div>
          <div data-assistant-anchor="save.pins">
          <StatTile
            to="/save/pinboard"
            label={intl.formatMessage({ id: "save.landing.pinsLabel", defaultMessage: "Pins" })}
            value={pinTotal}
            sub={
              pinTotal
                ? intl.formatMessage({ id: "save.landing.pinsSaved", defaultMessage: "saved" })
                : intl.formatMessage({ id: "save.landing.noPinsYet", defaultMessage: "nothing pinned yet" })
            }
            loading={insightsQ.loading && !insightsQ.data}
            actions={
              <QuickActionLink to="/save/pinboard" icon="→">
                <FormattedMessage id="save.landing.viewPins" defaultMessage="View pins" />
              </QuickActionLink>
            }
          />
          </div>
        </div>
      </SectionH>

      <SectionH
        label={intl.formatMessage({ id: "save.landing.vaultModulesLabel", defaultMessage: "Your Vault · 4 modules" })}
        right="PROFILE · SERVICE · COMMUNITY · PINS"
      >
        <NoBackend
          label={intl.formatMessage({ id: "save.landing.vaultModules", defaultMessage: "Vault modules" })}
          comingSoon
        />
      </SectionH>

      <SectionH
        label={intl.formatMessage({
          id: "save.landing.powerMapLabel",
          defaultMessage: "Your Power Map · Ready for MATCH",
        })}
        right={`${intl.formatMessage({ id: "save.landing.viewAll82", defaultMessage: "View all 82" })} ↗`}
      >
        <NoBackend
          label={intl.formatMessage({ id: "save.landing.powerMap", defaultMessage: "Power Map" })}
          comingSoon
        />
      </SectionH>

      <SectionH
        label={intl.formatMessage({
          id: "save.landing.cockpitLabel",
          defaultMessage: "Cockpit · Data flow at a glance",
        })}
        right={intl.formatMessage({
          id: "save.landing.cockpitRight",
          defaultMessage: "Visibility, Consent, Sync",
        })}
      >
        <NoBackend
          label={intl.formatMessage({
            id: "save.landing.visibilityMatrix",
            defaultMessage: "Visibility Matrix & Recent Activity",
          })}
          comingSoon
        />
      </SectionH>
    </main>
  );
}

function SectionH({
  label,
  right,
  children,
}: {
  label: string;
  right: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-10">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-[10px] tracking-[0.25em] text-ink/55">{label}</div>
        <div className="text-[10px] tracking-[0.2em] text-ink/40">{right}</div>
      </div>
      {children}
    </div>
  );
}
